import { Alert, ComboBox, flash, FormGroup, Label, Modal, SubmitButton } from "@xola/ui-kit";
import { useMemoizedFn } from "ahooks";
import pluralize from "pluralize";
import React, { useRef, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { EventOption } from "types";
import { api } from "@/api/api";
import { NotifyCustomerCheckbox } from "@/components/Common/NotifyCustomerCheckbox";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { useExperience } from "@/hooks/resources/useProduct";
import { useInvalidate } from "@/hooks/useInvalidation";
import { getEventOptionRow, OptionTargetEvent } from "@/models/Event";
import { getActiveOptions, isOptionsEnabled, OptionChoice, toOptionChoices } from "@/models/Experience";

interface ApplyExperienceOptionModalProps {
    events: OptionTargetEvent[];
    isOpen: boolean;
    onClose: () => void;
}

export const ApplyExperienceOptionModal = ({ events, isOpen, onClose }: ApplyExperienceOptionModalProps) => {
    const isApplyingRef = useRef(false);

    if (events.length === 0) {
        return null;
    }

    const handleClose = () => {
        if (isApplyingRef.current) {
            return;
        }
        onClose();
    };

    return (
        <Modal shouldCloseOnOutsideClick={false} isOpen={isOpen} onClose={handleClose}>
            <ModalContent events={events} isApplyingRef={isApplyingRef} onClose={onClose} />
        </Modal>
    );
};

const ModalContent = ({
    events,
    onClose,
    isApplyingRef,
}: {
    events: OptionTargetEvent[];
    onClose: () => void;
    isApplyingRef: React.MutableRefObject<boolean>;
}) => {
    const invalidate = useInvalidate();
    const queryClient = useQueryClient();
    const experienceId = events[0].experience.id;
    const { experience } = useExperience(experienceId);
    const options = isOptionsEnabled(experience) ? getActiveOptions(experience) : [];

    const [selectedOptionId, setSelectedOptionId] = useState<string | undefined>(
        events.length === 1 ? getEventOptionRow(events[0])?.optionId : undefined,
    );
    const [shouldNotifyCustomer, setShouldNotifyCustomer] = useState(true);
    const handleOptionChange = useMemoizedFn((choice: OptionChoice | null) => setSelectedOptionId(choice?.value));
    const handleNotifyChange = useMemoizedFn((event: React.ChangeEvent<HTMLInputElement>) =>
        setShouldNotifyCustomer(event.target.checked),
    );
    // Unset when the pre-selected Option was archived, so Apply stays disabled rather than re-applying it.
    const selectedOption = options.find((option) => option.id === selectedOptionId);
    const eventsToUpdate = events.filter((event) => getEventOptionRow(event)?.optionId !== selectedOptionId);

    const choices = toOptionChoices(options);
    const selectedChoice = selectedOption ? { value: selectedOption.id, label: selectedOption.name } : null;

    // Settled rather than all: one failed event must not leave the others un-invalidated.
    const { mutateAsync: apply, isLoading } = useMutation(async () => {
        const results = await Promise.allSettled(
            eventsToUpdate.map(async (event) => {
                const body = {
                    optionId: selectedOptionId,
                    _emailOverrides: { sendEmailToTraveler: shouldNotifyCustomer },
                };
                // A turned-off Event Option is reused too; only an event with none gets one created.
                const eventOptionId = event.options?.[0]?.id;
                return eventOptionId
                    ? api.put<EventOption>(`/events/${event.id}/options/${eventOptionId}`, body)
                    : api.post<EventOption>(`/events/${event.id}/options`, body);
            }),
        );
        return results.filter((result) => result.status === "rejected").length;
    });

    const handleApply = async () => {
        isApplyingRef.current = true;
        try {
            const failedCount = await apply();
            invalidate("events");
            for (const event of eventsToUpdate) {
                queryClient.invalidateQueries(["events", event.id]);
            }
            invalidate("timeslots");
            if (failedCount > 0) {
                flash.danger(
                    `Could not apply the Experience Option to ${failedCount} ${pluralize("event", failedCount)}`,
                );
                return;
            }
            flash.success(
                `Experience Option applied to ${eventsToUpdate.length} ${pluralize("event", eventsToUpdate.length)}`,
            );
            onClose();
        } finally {
            isApplyingRef.current = false;
        }
    };

    return (
        <>
            <Modal.Header>Apply Experience Option</Modal.Header>
            <Modal.Body className="space-y-4">
                <Alert color="warning" data-testid="apply-experience-option-alert">
                    This Experience Option will be applied to{" "}
                    {events.length === 1 ? "this event" : <var data-var="eventCount">{events.length} events</var>}. Any
                    existing Experience Options will be overwritten.
                </Alert>

                <FormGroup>
                    <Label>Name</Label>
                    <ComboBox
                        data-testid="apply-experience-option-picker"
                        options={choices}
                        value={selectedChoice}
                        placeholder="Select an Experience Option"
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 30 }) }}
                        onChange={handleOptionChange}
                    />
                </FormGroup>
            </Modal.Body>
            <Modal.Footer>
                <div className="flex items-center justify-between">
                    <NotifyCustomerCheckbox isChecked={shouldNotifyCustomer} onChange={handleNotifyChange} />
                    <div className="flex items-center space-x-2">
                        <SecondaryButton
                            data-testid="apply-experience-option-cancel-button"
                            disabled={isLoading}
                            onClick={onClose}
                        >
                            Cancel
                        </SecondaryButton>
                        <SubmitButton
                            data-testid="apply-experience-option-submit-button"
                            isLoading={isLoading}
                            disabled={!selectedOption || eventsToUpdate.length === 0}
                            onClick={handleApply}
                        >
                            Apply
                        </SubmitButton>
                    </div>
                </div>
            </Modal.Footer>
        </>
    );
};
